import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Header from "../../components/Header";
import PageBanner from "../../components/PageBanner";
import Footer from "../../components/Footer";
import "./pgcetLanding.css";
import { Helmet } from "react-helmet";

/* ==========================================================================
   Scroll reveal — IntersectionObserver with a visibility failsafe.
   The element is hidden only while JS is actively driving it; a timer
   guarantees it becomes visible even in headless / hidden-tab renders, so a
   section can never ship blank.
   ========================================================================== */
const Reveal = ({ children, className = "", delay = 0, as: Tag = "div" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const failsafe = setTimeout(() => setShown(true), 1400);
    if (!("IntersectionObserver" in window)) {
      setShown(true);
      return () => clearTimeout(failsafe);
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
            clearTimeout(failsafe);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimeout(failsafe);
    };
  }, []);

  const MotionTag = Tag;
  return (
    <MotionTag ref={ref} className={`pgc-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </MotionTag>
  );
};

/* ---------- icons (stroke, currentColor) ---------- */
const svg = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
const IconExternal = (<svg {...svg}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><path d="M15 3h6v6" /><path d="M10 14 21 3" /></svg>);
const IconArrow = (<svg {...svg}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>);
const IconCheck = (<svg {...svg}><path d="M20 6 9 17l-5-5" /></svg>);
const IconCap = (<svg {...svg}><path d="M22 10 12 5 2 10l10 5 10-5Z" /><path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" /></svg>);
const IconSeat = (<svg {...svg}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
const IconAward = (<svg {...svg}><circle cx="12" cy="8" r="6" /><path d="M15.5 12.5 17 22l-5-3-5 3 1.5-9.5" /></svg>);
const IconRoute = (<svg {...svg}><circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" /></svg>);

/* ---------- content (from pg.ncet.co.in) ---------- */
const QUICK_FACTS = [
  { icon: IconCap, value: "3", label: "PG Programmes" },
  { icon: IconSeat, value: "198", label: "Total Seats" },
  { icon: IconAward, value: "A+", label: "NAAC Accredited" },
  { icon: IconRoute, value: "KEA", label: "PGCET Admission" },
];

const IMPORTANT_DATES = [
  { event: "PGCET 2026 Exam (MBA/MCA)", date: "June 2026" },
  { event: "Provisional Answer Key", date: "Mid-June 2026" },
  { event: "Objection Window", date: "June 2026" },
  { event: "Result Declaration", date: "To Be Announced" },
  { event: "Document Verification", date: "To Be Announced" },
  { event: "First Round Option Entry", date: "To Be Announced" },
];

const PORTALS = [
  { label: "Objection Portal", href: "https://cetonline.karnataka.gov.in/keaobjections/forms/login.aspx" },
  { label: "OMR Data", href: "https://cetonline.karnataka.gov.in/keaomrs/kries.aspx" },
  { label: "Official KEA", href: "https://cetonline.karnataka.gov.in/kea/" },
];

const ELIGIBILITY = [
  {
    course: "MBA",
    full: "Master of Business Administration",
    criteria:
      "Bachelor's degree with a minimum of 50% marks (45% for SC/ST/OBC). Must appear for PGCET / KMAT / CMAT / MAT.",
    tests: ["PGCET", "KMAT", "CMAT", "MAT"],
  },
  {
    course: "MCA",
    full: "Master of Computer Applications",
    criteria:
      "BCA / Bachelor's degree in Computer Science Engineering or equivalent with a minimum of 50% marks (45% for SC/ST/OBC).",
    tests: ["PGCET"],
  },
  {
    course: "M.Tech",
    full: "Structural Engineering",
    criteria:
      "B.E / B.Tech in the relevant branch with a minimum of 50% marks (45% for SC/ST/OBC). Valid GATE score or PGCET rank.",
    tests: ["GATE", "PGCET"],
  },
];

const APPLICATION_STEPS = [
  { title: "Register on KEA", text: "Create your account and register online on the official KEA portal." },
  { title: "Fill Application", text: "Complete the application form and upload the necessary documents." },
  { title: "Pay the Fee", text: "Pay the prescribed application fee to confirm your submission." },
  { title: "Download Hall Ticket", text: "Download the admission ticket (hall ticket) ahead of the exam." },
  { title: "Appear for PGCET", text: "Sit the Karnataka PGCET examination at your allotted centre." },
  { title: "Document Verification", text: "Attend verification at a designated PGCET helpline centre." },
  { title: "Option Entry & Allotment", text: "Participate in option entry and seat allotment rounds." },
];

const PG_PROGRAMS = [
  {
    name: "Master of Business Administration",
    short: "MBA",
    intake: 120,
    duration: "2 Years",
    pgcetCode: "B228",
    hod: {
      name: "Dr. Geethanjali G",
      designation: "Professor & HOD",
      message:
        "It gives me immense pleasure to lead the Department of MBA at Nagarjuna College of Engineering and Technology. Industry 4.0 is driving and reshaping our future and how humans can think about the future jobs and its requirements. It has created a highly transformative impact on digital, data driven and interconnected industry and is guiding principal for innovation and advancement. With the gradual shift towards Industry 5.0 the focus is now being on with human centricity, sustainability and resilience. It is very imperative for the students to learn and develop the skills of future jobs and its environment and at Nagarjuna, our primary focus of the department is to nurture and empower students with multi-dimensional skill-set that create strong ethical and socially responsible leaders for tomorrow.",
    },
    about:
      "The two-year master’s course in Business Administration strives to inculcate in the students the values of excellence, opening up a whole new dimension of opportunities to excel and succeed in the corporate world.",
    features: ["HR", "Finance", "Marketing", "Logistics and Supply Chain Management", "Banking and Insurance"],
  },
  {
    name: "Master of Computer Applications",
    short: "MCA",
    intake: 60,
    duration: "2 Years",
    hod: {
      name: "Dr. Parthasarathi Murugesan",
      designation: "Professor & HOD",
      message:
        "Welcome to the Department of Computer Applications, offering both BCA and MCA programs. Our department is committed to nurturing future-ready IT professionals who are equipped with strong technical foundations, analytical abilities, and industry-aligned skills. With a dedicated team of experienced faculty, state-of-the-art laboratories, and a curriculum designed to meet emerging technological trends, we strive to create an environment that encourages innovation, research, and holistic development. We continuously emphasize hands-on learning, industry interaction, and real-time project exposure to ensure our students gain the confidence and competence required in today’s dynamic digital landscape. I warmly invite you to explore our department and join us in our mission to build skilled, ethical, and responsible technology professionals.",
    },
    about:
      "The MCA is a postgraduate degree programme designed to equip students with fundamental computer science knowledge on advanced computer applications development. Focuses on cutting-edge technologies, software engineering, AI & ML, cloud computing, cybersecurity, and database management.",
    features: [
      "Competency-Based Education with Industry Specific Curriculum",
      "Coding and Skill Training Ecosystem: Tech Hub",
      "AI-Powered Personalized Learning Plans & Assessment",
      "Global Certification Programmes",
      "Corporate and R&D based Internships",
      "Promoting Entrepreneurship and Start-up Culture",
    ],
  },
  {
    name: "M.Tech in Structural Engineering",
    short: "M.Tech",
    intake: 18,
    duration: "2 Years",
    pgcetCode: "T849",
    accreditation: "Institution is Accredited by NAAC with A+ Grade",
    hod: {
      name: "Dr. M M Premchand",
      designation: "Professor & Head of Department",
      message:
        "I heartily welcome all the students and their parents to the M.Tech Structural Engineering program. Structural Engineering is the backbone of today's infrastructure development. We are committed to provide not only technical education but also develop leadership qualities through which our graduates can create significant impact in the field. During their study at the department, students are encouraged to get hands-on experience through internship projects with reputed organizations and research collaborations. We believe our graduates are well-equipped to excel in their professional roles and consistently exceed expectations in the industry. The curriculum encourages students to undertake research projects to supplement theoretical knowledge with practical experience. We provide opportunities to participate in workshops, seminars, technical fests, and activities of social relevance. I congratulate the faculty members for their dedication and wish all students a successful academic journey.",
    },
    about:
      "The Course aims to transform the learners into an expert in technical knowledge by providing excellent academic environment which helps in grooming the innovative and challenging future.",
    features: [
      "Understanding the basics and opting for excellence",
      "Giving attention to issues of national and global relevance",
      "Experiential learning with insights from qualified mentors",
      "Well-equipped laboratories for teaching, R & D and industrial consultancy works",
    ],
  },
];

const TRAINING_PLAN = [
  "E-box Training Platform & Mock Interviews",
  "Domain Knowledge & Industry Related Training",
  "Presentation & Time Management Skills",
  "Group Discussion & Resume Writing Skills",
];

const X_FACTORS = ["Flexible CBCS", "Certification Courses", "MOOCs", "Corporate Internships", "Project Based Learning"];

const EASE = [0.22, 1, 0.36, 1];
const isAwaiting = (d) => /announced/i.test(d);

/* ---------- reusable section header ---------- */
const SectionLabel = ({ kicker, title, note }) => (
  <div className="pgc-sechead">
    <span className="pgc-kicker">{kicker}</span>
    <h2 className="pgc-title">{title}</h2>
    {note && <p className="pgc-note">{note}</p>}
  </div>
);

const PgcetLanding = () => {
  const [activeCourse, setActiveCourse] = useState(0);
  const [activeProg, setActiveProg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "PGCET Counseling 2026 | NCET";
  }, []);

  // Helmet is used to inject the chatbot script into the head/body natively

  const elig = ELIGIBILITY[activeCourse];
  const prog = PG_PROGRAMS[activeProg];

  return (
    <div className="pgc">
      <Helmet>
        <script src="https://chat.stepvista.in/widget.js" data-tenant="ncet" async></script>
      </Helmet>

      <Header parentMenu="study" topbarEnable="enable" />

      <PageBanner
        eyebrow="Admissions 2026"
        title="PGCET Counseling"
        subtitle="Your complete guide to admission at NCET Bangalore through the Karnataka PGCET — for MBA, MCA and M.Tech Structural Engineering."
      />

      {/* ---------- overlapping quick-facts ribbon ---------- */}
      <div className="pgc-shell">
        <div className="pgc-ribbon">
          {QUICK_FACTS.map((f, i) => (
            <div className="pgc-ribbon__item" key={i}>
              <span className="pgc-ribbon__ic" aria-hidden="true">{f.icon}</span>
              <span className="pgc-ribbon__value">{f.value}</span>
              <span className="pgc-ribbon__label">{f.label}</span>
            </div>
          ))}
        </div>
      </div>

      <main className="pgc-main">
        {/* ================= TIMELINE — horizontal milestone ribbon ================= */}
        <section id="timeline" className="pgc-section">
          <div className="pgc-sechead-row">
            <SectionLabel kicker="Timeline" title="Key Dates & Schedule" note="Track every PGCET milestone from exam day through option entry." />
            <div className="pgc-portals">
              {PORTALS.map((p, i) => (
                <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" className="pgc-portalbtn">
                  {p.label} <span className="pgc-ic-14">{IconExternal}</span>
                </a>
              ))}
            </div>
          </div>

          <Reveal className="pgc-track-wrap">
            <div className="pgc-track" role="list">
              <span className="pgc-track__rail" aria-hidden="true" />
              {IMPORTANT_DATES.map((item, i) => {
                const awaiting = isAwaiting(item.date);
                return (
                  <div className="pgc-ms" role="listitem" key={i} style={{ transitionDelay: `${i * 70}ms` }}>
                    <span className={`pgc-ms__node ${awaiting ? "is-await" : "is-set"}`} aria-hidden="true">
                      <span className="pgc-ms__dot" />
                    </span>
                    <span className="pgc-ms__body">
                      <span className="pgc-ms__date">{item.date}</span>
                      <span className="pgc-ms__event">{item.event}</span>
                      <span className={`pgc-ms__status ${awaiting ? "is-await" : "is-set"}`}>
                        {awaiting ? "Awaiting" : "Scheduled"}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </section>

        {/* ================= ELIGIBILITY — segmented switcher ================= */}
        <section id="eligibility" className="pgc-section">
          <SectionLabel kicker="Requirements" title="Eligibility & Cutoffs" note="Select a programme to see who can apply and which entrance tests count." />

          <div className="pgc-elig">
            <Reveal className="pgc-elig__panel">
              <div className="pgc-seg" role="tablist" aria-label="Programme eligibility">
                {ELIGIBILITY.map((e, i) => (
                  <button
                    key={i}
                    role="tab"
                    aria-selected={activeCourse === i}
                    className={`pgc-seg__btn ${activeCourse === i ? "is-active" : ""}`}
                    onClick={() => setActiveCourse(i)}
                  >
                    {e.course}
                  </button>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCourse}
                  className="pgc-elig__body"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3, ease: EASE }}
                >
                  <p className="pgc-elig__full">{elig.full}</p>
                  <p className="pgc-elig__crit">{elig.criteria}</p>
                  <div className="pgc-elig__tests">
                    <span className="pgc-elig__tests-lbl">Accepted entrance tests</span>
                    <div className="pgc-elig__chips">
                      {elig.tests.map((t, i) => (
                        <span className="pgc-testchip" key={i}>{t}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </Reveal>

            <Reveal className="pgc-cutoff" delay={80}>
              <span className="pgc-cutoff__mark" aria-hidden="true">%</span>
              <h3 className="pgc-cutoff__title">Previous Year Cutoffs</h3>
              <p className="pgc-cutoff__text">
                Cutoffs shift every year with exam difficulty, the number of applicants, and category reservations.
                Refer to the official KEA portal for verified figures.
              </p>
              <a href="https://cetonline.karnataka.gov.in/kea/" target="_blank" rel="noopener noreferrer" className="pgc-textlink">
                Check Official KEA Updates <span className="pgc-ic-16">{IconArrow}</span>
              </a>
            </Reveal>
          </div>
        </section>

        {/* ================= APPLICATION — vertical connected journey ================= */}
        <section id="process" className="pgc-section">
          <SectionLabel kicker="How to Apply" title="The Application Journey" note="Seven steps from registration to seat allotment." />

          <ol className="pgc-journey">
            <span className="pgc-journey__rail" aria-hidden="true" />
            {APPLICATION_STEPS.map((step, i) => (
              <Reveal as="li" className="pgc-jstep" key={i} delay={i * 40}>
                <span className="pgc-jstep__num" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                <div className="pgc-jstep__body">
                  <h3 className="pgc-jstep__title">{step.title}</h3>
                  <p className="pgc-jstep__text">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </section>

        {/* ================= PROGRAMMES — tab-rail + detail panel ================= */}
        <section id="programmes" className="pgc-section">
          <SectionLabel kicker="Academics" title="Explore PG Programmes" note="Three postgraduate pathways, each led by an experienced department head." />

          <Reveal className="pgc-progs">
            {/* rail */}
            <div className="pgc-progs__rail" role="tablist" aria-label="Postgraduate programmes">
              {PG_PROGRAMS.map((p, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={activeProg === i}
                  className={`pgc-progtab ${activeProg === i ? "is-active" : ""}`}
                  onClick={() => setActiveProg(i)}
                >
                  <span className="pgc-progtab__short">{p.short}</span>
                  <span className="pgc-progtab__name">{p.name}</span>
                  <span className="pgc-progtab__meta">{p.intake} seats · {p.duration}</span>
                </button>
              ))}
            </div>

            {/* detail */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProg}
                className="pgc-detail"
                role="tabpanel"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.32, ease: EASE }}
              >
                <div className="pgc-detail__head">
                  <h3 className="pgc-detail__name">{prog.name}</h3>
                  <div className="pgc-detail__facts">
                    <span><b>{prog.duration}</b> Duration</span>
                    <span><b>{prog.intake}</b> Seats</span>
                    {prog.pgcetCode && <span><b>{prog.pgcetCode}</b> PGCET Code</span>}
                  </div>
                </div>

                <p className="pgc-detail__about">{prog.about}</p>

                <div className="pgc-detail__grid">
                  <div className="pgc-detail__highlights">
                    <h4 className="pgc-detail__subh">Programme Highlights</h4>
                    <ul>
                      {prog.features.map((f, i) => (
                        <li key={i}>
                          <span className="pgc-tick" aria-hidden="true">{IconCheck}</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <figure className="pgc-hod">
                    <span className="pgc-hod__quote" aria-hidden="true">&ldquo;</span>
                    <blockquote className="pgc-hod__msg">{prog.hod.message}</blockquote>
                    <figcaption className="pgc-hod__cap">
                      <span className="pgc-hod__avatar" aria-hidden="true">{IconCap}</span>
                      <span>
                        <span className="pgc-hod__name">{prog.hod.name}</span>
                        <span className="pgc-hod__role">{prog.hod.designation}</span>
                      </span>
                    </figcaption>
                  </figure>
                </div>
              </motion.div>
            </AnimatePresence>
          </Reveal>
        </section>

        {/* ================= PLACEMENTS — navy bento finale ================= */}
        <section id="placements" className="pgc-section pgc-section--last">
          <Reveal className="pgc-place">
            <div className="pgc-place__glow pgc-place__glow--a" aria-hidden="true" />
            <div className="pgc-place__glow pgc-place__glow--b" aria-hidden="true" />

            <div className="pgc-place__grid">
              <div className="pgc-place__lead">
                <span className="pgc-place__tag">Placements &amp; Training</span>
                <h2 className="pgc-place__title">Phenomenal Placements</h2>
                <p className="pgc-place__desc">
                  At NCET, training our students for a brighter future is a core component of our course structure. We identify
                  potential talent through various assessments and formulate personalized training plans for every student.
                </p>
                <div className="pgc-place__stats">
                  <div className="pgc-place__stat">
                    <span className="pgc-place__stat-v">20 LPA</span>
                    <span className="pgc-place__stat-l">Highest Package</span>
                  </div>
                  <div className="pgc-place__stat">
                    <span className="pgc-place__stat-v">350+</span>
                    <span className="pgc-place__stat-l">Placed (2023)</span>
                  </div>
                </div>
              </div>

              <div className="pgc-place__box">
                <h3 className="pgc-place__boxh">Training Plan Includes</h3>
                <ul className="pgc-place__list">
                  {TRAINING_PLAN.map((item, i) => (
                    <li key={i}>
                      <span className="pgc-tick pgc-tick--on-dark" aria-hidden="true">{IconCheck}</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pgc-place__xf">
                <h3 className="pgc-place__boxh">The X-Factor of NCET</h3>
                <div className="pgc-place__chips">
                  {X_FACTORS.map((item, i) => (
                    <span className="pgc-xchip" key={i}>{item}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PgcetLanding;
